/**
 * Application Constants
 *
 * Centralized location for magic numbers, strings, and configuration values.
 * This improves maintainability and makes values easy to find and update.
 */

// =============================================================================
// Financial Rates & Defaults
// =============================================================================

export const FINANCIAL_RATES = {
  /** Default property tax rate (2% of property value annually) */
  PROPERTY_TAX_RATE: 0.02,

  /** Default insurance rate (0.25% of property value annually) */
  INSURANCE_RATE: 0.0025,

  /** Default closing cost rate (3% of purchase price) */
  CLOSING_COST_RATE: 0.03,

  /** Default rent-to-price ratio for estimating rent */
  RENT_TO_PRICE_RATIO: 0.006,

  /** Default vacancy rate */
  VACANCY_RATE: 0.05,

  /** Default property management fee rate */
  PROPERTY_MANAGEMENT_RATE: 0.08,

  /** Default maintenance reserve rate */
  MAINTENANCE_RATE: 0.01,

  /** Default appreciation rate for projections */
  APPRECIATION_RATE: 0.03,

  /** Default rent growth rate for projections */
  RENT_GROWTH_RATE: 0.02,
} as const;

export const LOAN_DEFAULTS = {
  /** Default interest rate */
  INTEREST_RATE: 6.5,

  /** Default loan term in years */
  TERM_YEARS: 30,

  /** Default down payment percentage */
  DOWN_PAYMENT_PERCENT: 0.25,

  /** Minimum down payment percentage */
  MIN_DOWN_PAYMENT_PERCENT: 0.03,

  /** Maximum loan-to-value ratio */
  MAX_LTV: 0.97,
} as const;

// =============================================================================
// Risk Thresholds
// =============================================================================

export const RISK_THRESHOLDS = {
  /** Cap rate thresholds */
  CAP_RATE: {
    LOW: 4,
    MEDIUM: 6,
    HIGH: 8,
  },

  /** Cash-on-cash return thresholds */
  CASH_ON_CASH: {
    LOW: 5,
    MEDIUM: 8,
    HIGH: 12,
  },

  /** Debt service coverage ratio thresholds */
  DSCR: {
    CRITICAL: 1.0,
    LOW: 1.25,
    HEALTHY: 1.5,
  },

  /** Loan-to-value thresholds */
  LTV: {
    LOW: 60,
    MEDIUM: 75,
    HIGH: 80,
  },

  /** Deal score thresholds */
  DEAL_SCORE: {
    POOR: 40,
    FAIR: 60,
    GOOD: 75,
    EXCELLENT: 90,
  },
} as const;

// =============================================================================
// API Configuration
// =============================================================================

// Lazy load config to avoid loading secrets on client-side
// This module can be imported in both client and server code
function getConfigLazy() {
  // Only load config on server-side
  if (typeof window === "undefined") {
    // Use synchronous import - getConfig() is synchronous
    // Import is safe here because this only runs on server-side
    // eslint-disable-next-line @typescript-eslint/no-require-imports
    const { getConfig } = require("@/config");
    return getConfig();
  }
  // On client-side, return a minimal config with only public values
  return {
    reactQuery: {
      staleTime: parseInt(
        process.env.NEXT_PUBLIC_QUERY_STALE_TIME_MS || "300000",
        10,
      ),
      cacheTime: parseInt(
        process.env.NEXT_PUBLIC_QUERY_CACHE_TIME_MS || "600000",
        10,
      ),
      refetchOnWindowFocus:
        process.env.NEXT_PUBLIC_REFETCH_ON_WINDOW_FOCUS === "true",
      refetchOnMount: process.env.NEXT_PUBLIC_REFETCH_ON_MOUNT === "true",
    },
    api: {
      timeout: parseInt(process.env.NEXT_PUBLIC_API_TIMEOUT_MS || "30000", 10),
    },
  };
}

// Get React Query config from centralized configuration (lazy loaded)
const config = getConfigLazy();

export const API_CONFIG = {
  /** Session cache TTL in milliseconds */
  SESSION_CACHE_TTL: 5000,

  /** React Query stale time in milliseconds (from centralized config) */
  QUERY_STALE_TIME: config.reactQuery.staleTime,

  /** React Query cache time in milliseconds (from centralized config) */
  QUERY_CACHE_TIME: config.reactQuery.cacheTime,

  /** Stale time for summary/aggregate data (longer cache) */
  SUMMARY_STALE_TIME: 300000, // 5 minutes

  /** Stale time for list data (shorter cache) */
  LIST_STALE_TIME: 60000, // 1 minute

  /** Refetch on window focus (from centralized config) */
  REFETCH_ON_WINDOW_FOCUS: config.reactQuery.refetchOnWindowFocus,

  /** Refetch on mount (from centralized config) */
  REFETCH_ON_MOUNT: config.reactQuery.refetchOnMount,

  /** Maximum retry attempts for failed requests */
  MAX_RETRIES: 2,

  /** Request timeout in milliseconds (from centralized config) */
  REQUEST_TIMEOUT: config.api.timeout,

  /** Slow request threshold in milliseconds */
  SLOW_REQUEST_THRESHOLD: 3000,

  /** Default pagination limit */
  DEFAULT_LIMIT: 100,
} as const;

// =============================================================================
// Rate Limiting
// =============================================================================

export const RATE_LIMIT_CONFIG = {
  /** Maximum requests per window */
  MAX_REQUESTS: 100,

  /** Window size in milliseconds */
  WINDOW_MS: 60000,

  /** Cleanup interval in milliseconds */
  CLEANUP_INTERVAL: 60000,
} as const;

// =============================================================================
// UI Constants
// =============================================================================

export const UI_CONFIG = {
  /** Default currency locale */
  CURRENCY_LOCALE: "en-US",

  /** Default currency */
  CURRENCY: "USD",

  /** Toast duration in milliseconds */
  TOAST_DURATION: 5000,

  /** Debounce delay for search inputs in milliseconds */
  SEARCH_DEBOUNCE: 300,

  /** Animation durations */
  ANIMATION: {
    FAST: 150,
    NORMAL: 300,
    SLOW: 500,
  },
} as const;

// =============================================================================
// Status Values
// =============================================================================

export const DEAL_STATUS = {
  ANALYZING: "analyzing",
  UNDER_CONTRACT: "under_contract",
  DUE_DILIGENCE: "due_diligence",
  CLOSED: "closed",
  DEAD: "dead",
  ON_HOLD: "on_hold",
} as const;

export const PORTFOLIO_STATUS = {
  ACTIVE: "active",
  SOLD: "sold",
  PENDING: "pending",
} as const;

export const OWNERSHIP_STRUCTURES = {
  SOLE: "sole",
  LLC: "llc",
  PARTNERSHIP: "partnership",
  DIRECT_CO_OWNERSHIP: "direct_co_ownership",
  TRUST: "trust",
} as const;

export const PROPERTY_TYPES = {
  SINGLE_FAMILY: "single_family",
  MULTI_FAMILY: "multi_family",
  CONDO: "condo",
  TOWNHOUSE: "townhouse",
  COMMERCIAL: "commercial",
  LAND: "land",
} as const;

export const LOAN_TYPES = {
  CONVENTIONAL: "conventional",
  FHA: "fha",
  VA: "va",
  USDA: "usda",
  JUMBO: "jumbo",
  ARM: "arm",
  INTEREST_ONLY: "interest_only",
} as const;

// =============================================================================
// Validation Limits
// =============================================================================

export const VALIDATION_LIMITS = {
  /** Maximum property price */
  MAX_PROPERTY_PRICE: 100_000_000,

  /** Minimum property price */
  MIN_PROPERTY_PRICE: 1000,

  /** Maximum interest rate */
  MAX_INTEREST_RATE: 30,

  /** Maximum loan term in years */
  MAX_LOAN_TERM: 40,

  /** Maximum bedrooms */
  MAX_BEDROOMS: 50,

  /** Maximum bathrooms */
  MAX_BATHROOMS: 50,

  /** Maximum square feet */
  MAX_SQUARE_FEET: 1_000_000,
} as const;

// =============================================================================
// Type Exports
// =============================================================================

export type DealStatus = (typeof DEAL_STATUS)[keyof typeof DEAL_STATUS];
export type PortfolioStatus =
  (typeof PORTFOLIO_STATUS)[keyof typeof PORTFOLIO_STATUS];
export type OwnershipStructure =
  (typeof OWNERSHIP_STRUCTURES)[keyof typeof OWNERSHIP_STRUCTURES];
export type PropertyType = (typeof PROPERTY_TYPES)[keyof typeof PROPERTY_TYPES];
export type LoanType = (typeof LOAN_TYPES)[keyof typeof LOAN_TYPES];
